import { useEffect, useState } from "react";
import { XIcon } from "lucide-react";
import type { PayrollStipendDraft } from "../../types";

type StipendFormProps = {
  isOpen: boolean;
  onClose: () => void;
  employeeName: string;
  initialValue?: PayrollStipendDraft | null;
  onSave?: (input: PayrollStipendDraft) => void | Promise<void>;
};

const PRESET_STIPEND_TYPES = [
  "Shabbaton / retreat chaperoning",
  "Coaching / athletics",
  "Committee chair / department head",
  "Curriculum development",
  "After-school / extracurricular program lead",
  "Carpool / bus duty",
  "Testing proctor / special events",
  "Travel allowance",
  "Davening / tefillah duties",
  "Substituting premium",
  "Custom",
] as const;

const today = () => new Date().toISOString().split("T")[0] ?? "";

export function StipendForm({
  isOpen,
  onClose,
  employeeName,
  initialValue,
  onSave,
}: StipendFormProps) {
  const [stipendType, setStipendType] = useState<string>(
    PRESET_STIPEND_TYPES[0],
  );
  const [customType, setCustomType] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [paymentSchedule, setPaymentSchedule] =
    useState<NonNullable<PayrollStipendDraft["paymentSchedule"]>>(
      "Spread Evenly Across Pay Periods",
    );
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [endDate, setEndDate] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const nextType = initialValue?.type ?? PRESET_STIPEND_TYPES[0];
    const isPreset = PRESET_STIPEND_TYPES.includes(nextType as any);
    setStipendType(isPreset ? nextType : "Custom");
    setCustomType(isPreset ? "" : nextType);
    setTotalAmount(
      initialValue?.totalAmount == null ? "" : String(initialValue.totalAmount),
    );
    setPaymentSchedule(
      initialValue?.paymentSchedule ?? "Spread Evenly Across Pay Periods",
    );
    setEffectiveDate(initialValue?.effectiveDate ?? today());
    setEndDate(initialValue?.endDate ?? "");
    setNotes(initialValue?.notes ?? "");
  }, [initialValue, isOpen]);

  if (!isOpen) {
    return null;
  }

  const finalType = stipendType === "Custom" ? customType : stipendType;
  const isValid =
    finalType !== "" &&
    totalAmount !== "" &&
    Number(totalAmount) > 0 &&
    endDate !== "";

  const handleSave = async () => {
    if (!isValid) {
      return;
    }
    await onSave?.({
      ...initialValue,
      type: finalType,
      totalAmount: Number(totalAmount),
      paymentSchedule,
      effectiveDate,
      endDate,
      notes: notes || undefined,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative flex max-h-[90vh] w-full max-w-[600px] flex-col overflow-hidden rounded-[12px] bg-surface-light shadow-xl animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between border-b border-border bg-muted/30 p-5">
          <div>
            <h2 className="font-heading text-lg font-semibold text-iron-grey">
              {initialValue?.id ? "Edit Stipend" : "Assign Stipend"}
            </h2>
            <p className="mt-0.5 font-body text-sm text-text-secondary">
              For {employeeName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary transition-colors hover:text-iron-grey"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto p-6">
          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
              Stipend Type
            </label>
            <select
              value={stipendType}
              onChange={(event) => setStipendType(event.target.value)}
              className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {PRESET_STIPEND_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {stipendType === "Custom" ? (
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Custom Stipend Name
              </label>
              <input
                type="text"
                value={customType}
                onChange={(event) => setCustomType(event.target.value)}
                placeholder="e.g. Yearbook advisor"
                maxLength={100}
                className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          ) : null}

          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
              Total Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-body text-sm text-text-secondary">
                $
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={totalAmount}
                onChange={(event) => setTotalAmount(event.target.value)}
                placeholder="0.00"
                className="w-full rounded-[6px] border border-border bg-surface-light py-2 pl-7 pr-3 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
              Payment Schedule
            </label>
            <select
              value={paymentSchedule}
              onChange={(event) =>
                setPaymentSchedule(
                  event.target
                    .value as NonNullable<PayrollStipendDraft["paymentSchedule"]>,
                )
              }
              className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="Spread Evenly Across Pay Periods">
                Spread Evenly Across Pay Periods
              </option>
              <option value="Lump Sum in Specific Period">
                Lump Sum in Specific Period
              </option>
              <option value="Custom Distribution">Custom Distribution</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Effective Date
              </label>
              <input
                type="date"
                value={effectiveDate}
                onChange={(event) => setEffectiveDate(event.target.value)}
                className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={3}
              placeholder="Internal notes about this stipend..."
              className="w-full resize-none rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-border bg-background-light p-5">
          <button
            onClick={onClose}
            className="rounded-[6px] border border-border bg-surface-light px-4 py-2 font-body text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            disabled={!isValid}
            onClick={() => void handleSave()}
            className="rounded-[6px] bg-primary px-4 py-2 font-body text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save Stipend
          </button>
        </div>
      </div>
    </div>
  );
}
